import React, { useState } from 'react';
import { Upload, Spin, Modal, List } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';

const { Dragger } = Upload;

/**
 * FileUploadComponent - A reusable React component for file upload using Ant Design's Upload.Dragger.
 *
 * This component allows users to upload multiple files and automatically dispatches the provided thunk action.
 * It supports continuous uploads while files are being processed.
 *
 * Props:
 * @param {function} thunkAction - A Redux thunk action to handle the uploaded file.
 * @param {function} [onSuccessCallback] - Optional callback function to be triggered after successful upload.
 * @param {Array} filesInProgress - Array of filenames currently being uploaded.
 */
const FileUploadComponent = ({ thunkAction, onSuccessCallback, filesInProgress }) => {
  const dispatch = useDispatch();
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [uploadedFilenames, setUploadedFilenames] = useState([]);

  console.log(filesInProgress);

  const handleFileChange = async (info) => {
    const { status, name } = info.file;

    if (status === 'done') {
      const file = info.file.originFileObj;
      try {
        await dispatch(thunkAction(file)).unwrap();
        setUploadedFilenames((prevFilenames) => [...prevFilenames, name]);
        setSuccessModalVisible(true);
        if (onSuccessCallback) {
          onSuccessCallback(name); // Trigger callback with filename if provided
        }
      } catch (error) {
        Modal.error({
          title: 'Upload Failed',
          content: `Failed to process file: ${error}`,
        });
      }
    } else if (status === 'error') {
      Modal.error({
        title: 'Upload Failed',
        content: `Failed to upload file: ${name}`,
      });
    }
  };

  return (
    <div className="file-upload-container">
      <div className="upload-section">
        <Dragger
          name="file"
          multiple={true}
          customRequest={({ file, onSuccess }) => {
            // Simulate custom request to allow Ant Design to continue the upload flow
            setTimeout(() => {
              onSuccess("ok");
            }, 0);
          }}
          onChange={handleFileChange}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag files to this area to upload</p>
          <p className="ant-upload-hint">Support for multiple uploads. Automatically submits upon selection.</p>
        </Dragger>
      </div>

      {/* Upload in Progress Section */}
      {filesInProgress && filesInProgress.length > 0 && (
        <div className="upload-in-progress">
          <h3>Uploads in Progress</h3>
          <List
            itemLayout="horizontal"
            dataSource={filesInProgress.map(file => file.name)}
            renderItem={(filename) => (
              <List.Item>
                <List.Item.Meta title={filename} />
                <Spin size="small" />
              </List.Item>
            )}
          />
        </div>
      )}

      <Modal
        visible={successModalVisible}
        title="Upload Successful"
        onOk={() => {
          setSuccessModalVisible(false);
          setUploadedFilenames([]);
        }}
        onCancel={() => {
          setSuccessModalVisible(false);
          setUploadedFilenames([]);
        }}
        okText="Got it"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {uploadedFilenames.map((filename, index) => (
          <p key={index} style={{ color: 'green' }}>{filename} has been successfully uploaded and processed.</p>
        ))}
      </Modal>

      {/* Optional Success Message Section */}
      {uploadedFilenames.length > 0 && (
        <div className="uploaded-files-list">
          <h3>Uploaded Files</h3>
          <List
            dataSource={uploadedFilenames}
            renderItem={(filename) => (
              <List.Item>
                <span style={{ color: 'green' }}>{filename}</span>
              </List.Item>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default FileUploadComponent;